import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  SignOut,
  Pencil,
  TrashSimple,
  Plus,
  House,
} from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";

const TABS = [
  { id: "training", label: "Træning", endpoint: "training" },
  { id: "news", label: "Nyheder", endpoint: "news" },
  { id: "matches", label: "Kampe", endpoint: "matches" },
  { id: "teams", label: "Hold", endpoint: "teams" },
  { id: "users", label: "Brugere", endpoint: "admin/users" },
];

const FIELDS = {
  training: [
    { name: "team", label: "Hold", required: true },
    { name: "day", label: "Dag", required: true, placeholder: "Mandag" },
    { name: "time", label: "Tid", required: true, placeholder: "19:00 – 20:30" },
    { name: "pitch", label: "Bane", required: true, placeholder: "Bane 1" },
    { name: "coach", label: "Træner", required: true },
  ],
  news: [
    { name: "title", label: "Titel", required: true },
    { name: "excerpt", label: "Resumé", required: true, type: "textarea" },
    { name: "date", label: "Dato", required: true, placeholder: "02. apr 2026" },
    { name: "category", label: "Kategori", required: true },
    { name: "href", label: "Link" },
    { name: "image", label: "Billede-URL" },
    { name: "featured", label: "Fremhævet", type: "checkbox" },
    { name: "order", label: "Rækkefølge", type: "number" },
  ],
  matches: [
    { name: "date", label: "Dato", required: true, placeholder: "2026-04-12" },
    { name: "time", label: "Tidspunkt", required: true, placeholder: "15:00" },
    { name: "home", label: "Hjemmehold", required: true },
    { name: "away", label: "Udehold", required: true },
    { name: "venue", label: "Spillested", required: true },
    {
      name: "type",
      label: "Type",
      required: true,
      type: "select",
      options: ["kamp", "resultat"],
    },
    { name: "competition", label: "Turnering", required: true },
    { name: "result_home", label: "Mål hjemme", type: "number" },
    { name: "result_away", label: "Mål ude", type: "number" },
  ],
  teams: [
    { name: "name", label: "Holdnavn", required: true },
    { name: "league", label: "Række/Liga", required: true },
    { name: "coach", label: "Cheftræner", required: true },
    { name: "assistant", label: "Assistent" },
    { name: "squad_size", label: "Trup-størrelse", type: "number" },
    { name: "category", label: "Kategori", required: true },
    { name: "order", label: "Rækkefølge", type: "number" },
  ],
  users: [
    { name: "name", label: "Navn", required: true },
    { name: "email", label: "Email", required: true, type: "email" },
    { name: "password", label: "Adgangskode", required: true, type: "password" },
    { name: "team_id", label: "Hold (UUID)", required: true },
  ],
};

const emptyFor = (tab) => {
  const f = FIELDS[tab];
  const obj = {};
  f.forEach((x) => {
    obj[x.name] = x.type === "checkbox" ? false : x.type === "number" ? 0 : "";
  });
  return obj;
};

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("training");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = none, "new" = new, or item

  useEffect(() => {
    if (user === false) nav("/login");
  }, [user, nav]);

  const fetchRows = async () => {
    const endpoint = TABS.find((t) => t.id === tab).endpoint;
    setLoading(true);
    try {
      const { data } = await api.get(`/${endpoint}`);
      setRows(data || []);
    } catch (e) {
      toast.error("Kunne ikke hente data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user]);

  const isUsersTab = tab === "users";

  if (user === null)
    return (
      <div className="grid min-h-screen place-items-center bg-[#0A0A0A] text-neutral-400">
        Loader…
      </div>
    );
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  const onDelete = async (id) => {
    if (!window.confirm("Slet denne post?")) return;
    try {
      const path = isUsersTab ? `/admin/users/${id}` : `/admin/${tab}/${id}`;
      await api.delete(path);
      toast.success("Slettet");
      fetchRows();
    } catch (e) {
      toast.error("Kunne ikke slette");
    }
  };

  const onSave = async (item) => {
    try {
      if (item.id && !isUsersTab) {
        await api.put(`/admin/${tab}/${item.id}`, item);
      } else {
        // For users tab, always POST (no update). For others, POST if no id.
        await api.post(isUsersTab ? "/admin/users" : `/admin/${tab}`, item);
      }
      toast.success("Gemt");
      setEditing(null);
      fetchRows();
    } catch (e) {
      toast.error(
        e.response?.data?.detail
          ? typeof e.response.data.detail === "string"
            ? e.response.data.detail
            : "Valideringsfejl"
          : "Kunne ikke gemme"
      );
    }
  };

  const columns = FIELDS[tab].slice(0, 4).map((f) => f.name);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-neutral-900 bg-black">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 place-items-center border border-brand bg-brand/10">
              <span className="block h-3 w-3 bg-brand" />
            </span>
            <span className="font-heading text-xl uppercase tracking-tight">
              TFC <span className="text-brand">Admin</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="mono-label hidden text-neutral-500 sm:inline">
              {user.email}
            </span>
            <a
              href="/"
              data-testid="admin-view-site"
              className="btn-ghost text-xs"
            >
              <House size={14} /> Vis site
            </a>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout"
              className="btn-ghost text-xs"
            >
              <SignOut size={14} /> Log ud
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        <h1 className="font-heading text-5xl uppercase tracking-tight">
          Klubadmin
        </h1>
        <p className="mt-2 font-body text-neutral-400">
          Rediger træning, nyheder, kampe og hold direkte fra panelet.
        </p>

        <div
          className="mt-8 flex flex-wrap gap-2"
          data-testid="admin-tabs"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`border px-4 py-2 font-mono text-xs uppercase tracking-widest transition-colors ${
                tab === t.id
                  ? "border-brand bg-brand text-black"
                  : "border-neutral-700 text-neutral-300 hover:border-brand hover:text-brand"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <p className="mono-label text-neutral-500">
            {rows.length} {tab === "matches" ? "kampe" : "poster"}
          </p>
          <button
            type="button"
            onClick={() => setEditing(emptyFor(tab))}
            data-testid="admin-create-btn"
            className="btn-primary text-xs"
          >
            <Plus size={14} weight="bold" /> Ny post
          </button>
        </div>

        <div
          data-testid="admin-table"
          className="mt-4 overflow-hidden border border-neutral-800"
        >
          <div className="hidden grid-cols-[1fr_1fr_1fr_1fr_160px] gap-0 border-b border-neutral-800 bg-black md:grid">
            {columns.map((c) => (
              <div key={c} className="mono-label px-5 py-3 text-neutral-500">
                {c}
              </div>
            ))}
            <div className="mono-label px-5 py-3 text-right text-neutral-500">
              Handling
            </div>
          </div>
          {loading && (
            <div className="p-6 text-center text-sm text-neutral-500">Loader…</div>
          )}
          {!loading && rows.length === 0 && (
            <div className="p-6 text-center text-sm text-neutral-500">
              Ingen poster endnu.
            </div>
          )}
          <div className="divide-y divide-neutral-900">
            {rows.map((r) => (
              <div
                key={r.id}
                data-testid={`admin-row-${r.id}`}
                className="grid grid-cols-2 gap-2 bg-black/40 px-5 py-4 hover:bg-black/70 md:grid-cols-[1fr_1fr_1fr_1fr_160px] md:gap-0 md:px-0"
              >
                {columns.map((c) => (
                  <div
                    key={c}
                    className="font-body text-sm text-neutral-200 md:px-5 md:py-1"
                  >
                    {String(r[c] ?? "—")}
                  </div>
                ))}
                <div className="col-span-2 mt-2 flex items-center justify-end gap-2 md:col-span-1 md:mt-0 md:px-5 md:py-1">
                  <button
                    type="button"
                    onClick={() => setEditing(r)}
                    data-testid={`admin-edit-${r.id}`}
                    className="inline-flex items-center gap-1 border border-neutral-700 px-3 py-1 font-mono text-xs uppercase hover:border-brand hover:text-brand"
                  >
                    <Pencil size={12} /> Ret
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(r.id)}
                    data-testid={`admin-delete-${r.id}`}
                    className="inline-flex items-center gap-1 border border-red-900/50 px-3 py-1 font-mono text-xs uppercase text-red-300 hover:border-red-500"
                  >
                    <TrashSimple size={12} /> Slet
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {editing && (
        <EditModal
          tab={tab}
          initial={editing}
          onCancel={() => setEditing(null)}
          onSave={onSave}
        />
      )}
    </div>
  );
}

function EditModal({ tab, initial, onCancel, onSave }) {
  const [form, setForm] = useState(initial);
  const fields = FIELDS[tab];

  const setVal = (name, v) => setForm((f) => ({ ...f, [name]: v }));

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    // ensure numbers
    fields.forEach((f) => {
      if (f.type === "number" && payload[f.name] !== "" && payload[f.name] !== null) {
        payload[f.name] = Number(payload[f.name]);
      }
    });
    onSave(payload);
  };

  return (
    <div
      data-testid="admin-edit-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
    >
      <form
        onSubmit={submit}
        className="hairline relative w-full max-w-2xl overflow-y-auto bg-neutral-950 p-6"
        style={{ maxHeight: "90vh" }}
      >
        <h2 className="font-heading text-2xl uppercase tracking-tight">
          {form.id ? "Rediger" : "Ny"} — {tab}
        </h2>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {fields.map((f) => (
            <div
              key={f.name}
              className={f.type === "textarea" ? "sm:col-span-2" : ""}
            >
              <label
                htmlFor={f.name}
                className="mono-label block text-neutral-400"
              >
                {f.label}
              </label>
              {f.type === "textarea" ? (
                <textarea
                  id={f.name}
                  rows={4}
                  required={f.required}
                  value={form[f.name] ?? ""}
                  onChange={(e) => setVal(f.name, e.target.value)}
                  data-testid={`admin-input-${f.name}`}
                  className="mt-2 w-full resize-none border border-neutral-800 bg-black px-3 py-2 font-body text-white outline-none focus:border-brand"
                />
              ) : f.type === "select" ? (
                <select
                  id={f.name}
                  required={f.required}
                  value={form[f.name] ?? f.options[0]}
                  onChange={(e) => setVal(f.name, e.target.value)}
                  data-testid={`admin-input-${f.name}`}
                  className="mt-2 w-full border border-neutral-800 bg-black px-3 py-2 font-body text-white outline-none focus:border-brand"
                >
                  {f.options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : f.type === "checkbox" ? (
                <label className="mt-3 inline-flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    checked={!!form[f.name]}
                    onChange={(e) => setVal(f.name, e.target.checked)}
                    data-testid={`admin-input-${f.name}`}
                    className="h-4 w-4 accent-brand"
                  />
                  <span className="font-body text-sm text-neutral-300">
                    {f.label}
                  </span>
                </label>
              ) : (
                <input
                  id={f.name}
                  type={f.type || "text"}
                  required={f.required}
                  placeholder={f.placeholder}
                  value={form[f.name] ?? ""}
                  onChange={(e) => setVal(f.name, e.target.value)}
                  data-testid={`admin-input-${f.name}`}
                  className="mt-2 w-full border border-neutral-800 bg-black px-3 py-2 font-body text-white outline-none focus:border-brand"
                />
              )}
            </div>
          ))}
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            data-testid="admin-modal-cancel"
            className="btn-ghost text-xs"
          >
            Annuller
          </button>
          <button
            type="submit"
            data-testid="admin-modal-save"
            className="btn-primary text-xs"
          >
            Gem
          </button>
        </div>
      </form>
    </div>
  );
}
